import { useWordHighlightColorToHex } from "./useWordHighlightColorToHex";
import { DOMParser as XMLDOMParser } from "@xmldom/xmldom";
import { useNormalizeWordDocumentLists } from "./useNormalizeWordDocumentLists";
import { useRestoreWordDocumentNumbering } from "./useRestoreWordDocumentNumbering";

/**
 * Parses Word document XML and applies styles to the HTML
 * Extracts paragraph properties (alignment, line height, indentation)
 * and run properties (underline, color, highlight)
 */
export const useParseWordDocumentStyles = (
  documentXml: string,
  html: string,
  stylesXml?: string,
  numberingXml?: string
): string => {
  type ParagraphStyleProps = {
    align?: string;
    line?: string;
    lineRule?: string;
    before?: string;
    after?: string;
    left?: string;
    right?: string;
    firstLine?: string;
    hanging?: string;
  };

  type RunStyleProps = {
    underline?: boolean;
  };

  const parseUnderlineValue = (
    underlineVal?: string | null
  ): boolean | undefined => {
    // Some Word files emit color-only w:u nodes for every run. Without an
    // explicit value, the node is metadata rather than an active underline.
    if (underlineVal == null) return undefined;

    return !["none", "false", "0"].includes(underlineVal.toLowerCase());
  };

  const mergeParagraphProps = (
    base: ParagraphStyleProps,
    overrides: ParagraphStyleProps
  ): ParagraphStyleProps => {
    const merged: ParagraphStyleProps = { ...base };

    for (const [key, value] of Object.entries(overrides)) {
      if (value !== undefined) {
        merged[key as keyof ParagraphStyleProps] = value;
      }
    }

    return merged;
  };

  const mergeRunProps = (
    base: RunStyleProps,
    overrides: RunStyleProps
  ): RunStyleProps => {
    const merged: RunStyleProps = { ...base };

    for (const [key, value] of Object.entries(overrides)) {
      if (value !== undefined) {
        merged[key as keyof RunStyleProps] = value;
      }
    }

    return merged;
  };

  const extractRunProps = (rPr?: Element | null): RunStyleProps => {
    if (!rPr) return {};

    const underlineNode = getFirst(rPr, "w:u");
    if (!underlineNode) {
      return {};
    }

    const underlineVal = getAttr(underlineNode, "w:val");

    return { underline: parseUnderlineValue(underlineVal) };
  };

  const twipsToPoints = (twipsValue: string): string => {
    const twips = parseInt(twipsValue, 10);
    if (!Number.isFinite(twips)) return "0pt";
    return `${(twips / 20).toFixed(2)}pt`;
  };

  const normalizeWordXml = (value: string) => {
    return value
      .replace(/\s+xmlns(:[A-Za-z0-9_-]+)?="[^"]*"/g, "")
      .replace(/(<\/?)([A-Za-z0-9_-]+:)([A-Za-z0-9_-]+)/g, "$1$3")
      .replace(/(\s)([A-Za-z0-9_-]+:)([A-Za-z0-9_-]+)=/g, "$1$3=");
  };

  const normalizeTagName = (tagName: string) => {
    return tagName.split(":").pop()?.toLowerCase() ?? tagName.toLowerCase();
  };

  const deprefixAttributeName = (attributeName: string) => {
    return attributeName.split(":").pop() ?? attributeName;
  };

  const getAttr = (element: Element | undefined | null, attributeName: string) => {
    if (!element) return null;

    const candidates = [
      attributeName,
      deprefixAttributeName(attributeName),
      normalizeTagName(attributeName),
    ];

    for (const candidate of candidates) {
      const value = element.getAttribute(candidate);
      if (value != null && value !== "") {
        return value;
      }
    }

    return null;
  };

  const escapeRegExp = (value: string) => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const getMarkupAttribute = (
    markup: string,
    tagName: string,
    attributeName: string
  ) => {
    const normalizedTagName = normalizeTagName(tagName);
    const normalizedAttributeName = deprefixAttributeName(attributeName);
    const regex = new RegExp(
      `<${escapeRegExp(normalizedTagName)}\\b[^>]*\\b${escapeRegExp(
        normalizedAttributeName
      )}="([^"]+)"`,
      "i"
    );

    return markup.match(regex)?.[1] ?? null;
  };

  const getElements = (parent: ParentNode, tagName: string): Element[] => {
    const normalizedTagName = normalizeTagName(tagName);
    const tagSearchParent = parent as ParentNode & {
      getElementsByTagName?: (name: string) => HTMLCollectionOf<Element>;
    };
    const allElements = Array.from(
      tagSearchParent.getElementsByTagName?.("*") ?? []
    );

    return allElements.filter((element) => {
      const elementTagName =
        element.tagName?.toLowerCase() ?? element.nodeName?.toLowerCase();
      const elementLocalName = element.localName?.toLowerCase();

      return (
        elementTagName === tagName.toLowerCase() ||
        elementLocalName === normalizedTagName ||
        normalizeTagName(elementTagName ?? "") === normalizedTagName
      );
    });
  };

  const getFirst = (parent: ParentNode, tagName: string): Element | undefined => {
    return getElements(parent, tagName)[0];
  };

  const extractParagraphProps = (pPr?: Element | null): ParagraphStyleProps => {
    if (!pPr) return {};
    const markup =
      typeof (pPr as any).toString === "function" ? (pPr as any).toString() : "";

    const jc = getFirst(pPr, "w:jc");
    const spacing = getFirst(pPr, "w:spacing");
    const ind = getFirst(pPr, "w:ind");

    return {
      align: getAttr(jc, "w:val") || getMarkupAttribute(markup, "jc", "val") || undefined,
      line:
        getAttr(spacing, "w:line") ||
        getMarkupAttribute(markup, "spacing", "line") ||
        undefined,
      lineRule:
        getAttr(spacing, "w:lineRule") ||
        getMarkupAttribute(markup, "spacing", "lineRule") ||
        undefined,
      before:
        getAttr(spacing, "w:before") ||
        getMarkupAttribute(markup, "spacing", "before") ||
        undefined,
      after:
        getAttr(spacing, "w:after") ||
        getMarkupAttribute(markup, "spacing", "after") ||
        undefined,
      left:
        getAttr(ind, "w:left") ||
        getMarkupAttribute(markup, "ind", "left") ||
        undefined,
      right:
        getAttr(ind, "w:right") ||
        getMarkupAttribute(markup, "ind", "right") ||
        undefined,
      firstLine:
        getAttr(ind, "w:firstLine") ||
        getMarkupAttribute(markup, "ind", "firstLine") ||
        undefined,
      hanging:
        getAttr(ind, "w:hanging") ||
        getMarkupAttribute(markup, "ind", "hanging") ||
        undefined,
    };
  };

  // Parse XML to extract paragraph and run properties
  const parser = new XMLDOMParser();
  const xmlDoc = parser.parseFromString(normalizeWordXml(documentXml), "text/xml");

  // Parse styles.xml to support style-based paragraph formatting.
  // This resolves paragraph style inheritance (w:basedOn) and doc defaults.
  let docDefaultProps: ParagraphStyleProps = {};
  const styleDefs: Record<
    string,
    { basedOn?: string; props: ParagraphStyleProps }
  > = {};
  const resolvedStyleCache: Record<string, ParagraphStyleProps> = {};
  let docDefaultRunProps: RunStyleProps = {};
  const runStyleDefs: Record<string, { basedOn?: string; props: RunStyleProps }> =
    {};
  const resolvedRunStyleCache: Record<string, RunStyleProps> = {};
  const rawRunStyleDefs: Record<
    string,
    { basedOn?: string; underline?: boolean }
  > = {};
  const resolvedRawRunStyleCache: Record<string, boolean | undefined> = {};

  if (stylesXml) {
    const normalizedStylesXml = normalizeWordXml(stylesXml);
    const stylesDoc = parser.parseFromString(normalizedStylesXml, "text/xml");

    const pPrDefault = getFirst(stylesDoc, "w:pPrDefault");
    const defaultPPr = pPrDefault ? getFirst(pPrDefault, "w:pPr") : undefined;
    docDefaultProps = extractParagraphProps(defaultPPr);
    const defaultRPr = getFirst(stylesDoc, "w:rPrDefault");
    docDefaultRunProps = extractRunProps(
      defaultRPr ? getFirst(defaultRPr, "w:rPr") : undefined
    );

    const styleNodes = getElements(stylesDoc, "w:style");
    for (let i = 0; i < styleNodes.length; i++) {
      const styleNode = styleNodes[i];
      const styleType = getAttr(styleNode, "w:type");
      const styleId = getAttr(styleNode, "w:styleId");
      const basedOn = getAttr(getFirst(styleNode, "w:basedOn"), "w:val");

      if (styleType === "paragraph" && styleId) {
        const stylePPr = getFirst(styleNode, "w:pPr");

        styleDefs[styleId] = {
          basedOn: basedOn || undefined,
          props: extractParagraphProps(stylePPr),
        };
      }

      if ((styleType === "character" || styleType === "paragraph") && styleId) {
        const styleRPr = getFirst(styleNode, "w:rPr");

        runStyleDefs[styleId] = {
          basedOn: basedOn || undefined,
          props: extractRunProps(styleRPr),
        };
      }
    }

    const styleRegex =
      /<style\b([^>]*)>([\s\S]*?)<\/style>/gi;
    let styleMatch: RegExpExecArray | null = null;

    while ((styleMatch = styleRegex.exec(normalizedStylesXml))) {
      const styleAttributes = styleMatch[1];
      const styleBody = styleMatch[2];
      const styleType =
        styleAttributes.match(/\btype="([^"]+)"/i)?.[1] ?? undefined;
      const styleId =
        styleAttributes.match(/\bstyleId="([^"]+)"/i)?.[1] ?? undefined;
      const basedOn = styleBody.match(/<basedOn\b[^>]*\bval="([^"]+)"/i)?.[1];

      if (
        (styleType === "character" || styleType === "paragraph") &&
        styleId &&
        runStyleDefs[styleId]?.props?.underline == undefined
      ) {
        const underlineVal =
          styleBody.match(/<u\b[^>]*\bval="([^"]+)"/i)?.[1] ?? null;
        const underline = parseUnderlineValue(underlineVal);

        runStyleDefs[styleId] = {
          basedOn: basedOn || runStyleDefs[styleId]?.basedOn,
          props: {
            underline,
          },
        };
        rawRunStyleDefs[styleId] = {
          basedOn: basedOn || rawRunStyleDefs[styleId]?.basedOn,
          underline,
        };
      } else if (
        (styleType === "character" || styleType === "paragraph") &&
        styleId
      ) {
        rawRunStyleDefs[styleId] = {
          basedOn: basedOn || rawRunStyleDefs[styleId]?.basedOn,
          underline: rawRunStyleDefs[styleId]?.underline,
        };
      }
    }
  }

  const resolveParagraphStyleProps = (
    styleId: string,
    seen: Set<string> = new Set()
  ): ParagraphStyleProps => {
    if (resolvedStyleCache[styleId]) {
      return resolvedStyleCache[styleId];
    }

    if (seen.has(styleId)) {
      return {};
    }

    const styleDef = styleDefs[styleId];
    if (!styleDef) {
      return {};
    }

    seen.add(styleId);

    let resolved: ParagraphStyleProps = {};
    if (styleDef.basedOn) {
      resolved = mergeParagraphProps(
        resolved,
        resolveParagraphStyleProps(styleDef.basedOn, seen)
      );
    }

    resolved = mergeParagraphProps(resolved, styleDef.props);
    resolvedStyleCache[styleId] = resolved;
    return resolved;
  };

  const resolveRunStyleProps = (
    styleId: string,
    seen: Set<string> = new Set()
  ): RunStyleProps => {
    if (resolvedRunStyleCache[styleId]) {
      return resolvedRunStyleCache[styleId];
    }

    if (seen.has(styleId)) {
      return {};
    }

    const styleDef = runStyleDefs[styleId];
    if (!styleDef) {
      return {};
    }

    seen.add(styleId);

    let resolved: RunStyleProps = {};
    if (styleDef.basedOn) {
      resolved = mergeRunProps(
        resolved,
        resolveRunStyleProps(styleDef.basedOn, seen)
      );
    }

    resolved = mergeRunProps(resolved, styleDef.props);
    resolvedRunStyleCache[styleId] = resolved;
    return resolved;
  };

  const resolveRawRunStyleUnderline = (
    styleId: string,
    seen: Set<string> = new Set()
  ): boolean | undefined => {
    if (styleId in resolvedRawRunStyleCache) {
      return resolvedRawRunStyleCache[styleId];
    }

    if (seen.has(styleId)) {
      return undefined;
    }

    const styleDef = rawRunStyleDefs[styleId];
    if (!styleDef) {
      return undefined;
    }

    seen.add(styleId);

    const inheritedUnderline = styleDef.basedOn
      ? resolveRawRunStyleUnderline(styleDef.basedOn, seen)
      : undefined;
    const underline = styleDef.underline ?? inheritedUnderline;

    resolvedRawRunStyleCache[styleId] = underline;
    return underline;
  };

  // Create a temporary DOM to manipulate the HTML
  const htmlParser = new DOMParser();
  const htmlDoc = htmlParser.parseFromString(`<div>${html}</div>`, "text/html");
  const htmlDiv = htmlDoc.querySelector("div");

  if (!htmlDiv) return html;

  // Get all paragraphs from Word XML
  const wordParagraphs = getElements(xmlDoc, "w:p");
  const htmlParagraphs = htmlDiv.querySelectorAll("p, h1, h2, h3, h4, h5, h6, li");

  // Match Word paragraphs with HTML paragraphs
  let htmlIndex = 0;
  for (let i = 0; i < wordParagraphs.length; i++) {
    const wordPara = wordParagraphs[i];

    // Check content to sync with Mammoth's output (which skips empty paragraphs)
    let hasContent = false;
    const checkRuns = getElements(wordPara, "w:r");
    for (let j = 0; j < checkRuns.length; j++) {
      const t = getFirst(checkRuns[j], "w:t");
      const drawing = getFirst(checkRuns[j], "w:drawing");
      const object = getFirst(checkRuns[j], "w:object");
      const tab = getFirst(checkRuns[j], "w:tab");
      // Mammoth emits tab-only and line-break-only paragraphs, but drops
      // page/column breaks. Consume their HTML blocks to keep styles in sync.
      const hasLineBreak = getElements(checkRuns[j], "w:br").some((br) => {
        const type = getAttr(br, "w:type");
        return !type || type === "textWrapping";
      });
      if ((t && t.textContent) || drawing || object || tab || hasLineBreak) {
        hasContent = true;
        break;
      }
    }

    let htmlPara: HTMLElement;

    if (hasContent) {
      if (htmlIndex >= htmlParagraphs.length) continue;
      htmlPara = htmlParagraphs[htmlIndex] as HTMLElement;
      htmlIndex++;
    } else {
      // Insert missing empty paragraph
      htmlPara = htmlDoc.createElement("p");
      htmlPara.innerHTML = "<br>";
      if (htmlIndex < htmlParagraphs.length) {
        const nextPara = htmlParagraphs[htmlIndex];
        nextPara.parentNode?.insertBefore(htmlPara, nextPara);
      } else {
        htmlDiv.appendChild(htmlPara);
      }
    }

    // Store tab information for later insertion (after processing runs)
    let tabText: string | null = null;

    // Extract paragraph properties (alignment, line height, indentation)
    const pPr = getFirst(wordPara, "w:pPr");

    const pStyle = pPr ? getFirst(pPr, "w:pStyle") : undefined;
    const paragraphStyleId = getAttr(pStyle, "w:val") ?? undefined;
    const directProps = extractParagraphProps(pPr);
    const styleProps = paragraphStyleId
      ? resolveParagraphStyleProps(paragraphStyleId)
      : {};
    const paragraphRunProps = paragraphStyleId
      ? resolveRunStyleProps(paragraphStyleId)
      : {};
    const effectiveProps = mergeParagraphProps(
      mergeParagraphProps(docDefaultProps, styleProps),
      directProps
    );

    // Get alignment
    const align = effectiveProps.align;
    if (align) {
      const alignMap: { [key: string]: string } = {
        left: "left",
        center: "center",
        right: "right",
        both: "justify",
        justify: "justify",
      };
      if (alignMap[align]) {
        htmlPara.style.textAlign = alignMap[align];
      }
    }

    // Get line spacing
    if (effectiveProps.line) {
      const lineRule = effectiveProps.lineRule || "auto";
      if (
        lineRule === "auto" ||
        lineRule === "exact" ||
        lineRule === "atLeast"
      ) {
        // Word stores line in 240ths of a line for auto, and twips for exact/atLeast.
        // Existing behavior treated both the same, which works for the current editor.
        const lineHeight = parseInt(effectiveProps.line, 10) / 240;
        if (Number.isFinite(lineHeight) && lineHeight > 0) {
          htmlPara.style.lineHeight = lineHeight.toFixed(2).toString();
        }
      }
    }

    // Paragraph spacing before/after (twips)
    if (effectiveProps.before) {
      htmlPara.style.marginTop = twipsToPoints(effectiveProps.before);
    }
    if (effectiveProps.after) {
      htmlPara.style.marginBottom = twipsToPoints(effectiveProps.after);
    }

    // Left/right indent
    if (effectiveProps.left) {
      htmlPara.style.marginLeft = twipsToPoints(effectiveProps.left);
    }
    if (effectiveProps.right) {
      htmlPara.style.marginRight = twipsToPoints(effectiveProps.right);
    }

    // Get first line indent and convert to tabs (store for later insertion)
    const firstLine = effectiveProps.firstLine;
    if (firstLine) {
      // firstLine is in twips (1/20 of a point)
      // Convert to approximate tabs (assuming 1 tab ≈ 0.5 inches ≈ 720 twips)
      const twips = parseInt(firstLine, 10);
      // Calculate tabs more accurately - ensure at least 1 tab for any indent
      const tabs = twips > 0 ? Math.max(1, Math.round(twips / 720)) : 0;

      if (tabs > 0) {
        // Use zero-width space + tab pattern to preserve tabs in HTML serialization
        tabText = "\u200B\t".repeat(tabs);
      }
    }

    const hanging = effectiveProps.hanging;
    if (hanging) {
      const hangingTwips = parseInt(hanging, 10);
      if (Number.isFinite(hangingTwips) && hangingTwips > 0) {
        htmlPara.style.textIndent = `-${twipsToPoints(hanging)}`;
        // Word's left indent already includes the hanging portion.
        if (!effectiveProps.left) {
          htmlPara.style.marginLeft = twipsToPoints(hanging);
        }
      }
    }

    // Extract run properties (underline, color, etc.)
    // Process runs BEFORE inserting tabs so text matching indices are correct
    const runs = getElements(wordPara, "w:r");
    const paraText = htmlPara.textContent || "";
    let leadingTabCount = 0;
    let encounteredVisibleContent = false;

    for (let j = 0; j < runs.length; j++) {
      const run = runs[j];
      const tabs = getElements(run, "w:tab");
      const t = getFirst(run, "w:t");
      const drawing = getFirst(run, "w:drawing");
      const object = getFirst(run, "w:object");

      if (
        !encounteredVisibleContent &&
        tabs.length > 0 &&
        !t &&
        !drawing &&
        !object
      ) {
        leadingTabCount += tabs.length;
        continue;
      }

      if ((t && t.textContent) || drawing || object) {
        encounteredVisibleContent = true;
      }
    }

    if (leadingTabCount > 0) {
      tabText = `${tabText ?? ""}${"\u200B\t".repeat(leadingTabCount)}`;
    }

    // Track processed text positions to handle duplicate text
    let processedOffset = 0;

    // Process each run and apply styles to matching text in HTML
    for (let j = 0; j < runs.length; j++) {
      const run = runs[j];
      const rPr = getFirst(run, "w:rPr");
      const t = getFirst(run, "w:t");

      // Check for page break
      const brs = getElements(run, "w:br");
      let hasPageBreak = false;
      for (let k = 0; k < brs.length; k++) {
        if (getAttr(brs[k], "w:type") === "page") {
          hasPageBreak = true;
          break;
        }
      }

      if (!hasPageBreak && (!t || !rPr)) continue;

      if (hasPageBreak) {
        const breakDiv = htmlDoc.createElement("div");
        breakDiv.setAttribute("data-page-break", "true");

        // Find insertion point using TreeWalker at processedOffset
        const walker = htmlDoc.createTreeWalker(
          htmlPara,
          NodeFilter.SHOW_TEXT,
          null
        );

        let currentNode: Node | null = null;
        let currentOffset = 0;
        let inserted = false;

        while ((currentNode = walker.nextNode())) {
          const nodeText = currentNode.textContent || "";
          const nodeStart = currentOffset;
          const nodeEnd = currentOffset + nodeText.length;

          if (processedOffset >= nodeStart && processedOffset <= nodeEnd) {
            const splitIndex = processedOffset - nodeStart;

            if (splitIndex === 0) {
              currentNode.parentNode?.insertBefore(breakDiv, currentNode);
            } else if (splitIndex === nodeText.length) {
              if (currentNode.nextSibling) {
                currentNode.parentNode?.insertBefore(
                  breakDiv,
                  currentNode.nextSibling
                );
              } else {
                currentNode.parentNode?.appendChild(breakDiv);
              }
            } else {
              const beforeText = nodeText.substring(0, splitIndex);
              const afterText = nodeText.substring(splitIndex);

              currentNode.textContent = beforeText;
              const afterNode = htmlDoc.createTextNode(afterText);

              if (currentNode.nextSibling) {
                currentNode.parentNode?.insertBefore(
                  afterNode,
                  currentNode.nextSibling
                );
                currentNode.parentNode?.insertBefore(breakDiv, afterNode);
              } else {
                currentNode.parentNode?.appendChild(breakDiv);
                currentNode.parentNode?.appendChild(afterNode);
              }
            }
            inserted = true;
            break;
          }
          currentOffset = nodeEnd;
        }

        if (!inserted) {
          htmlPara.appendChild(breakDiv);
        }
      }

      if (!t || !rPr) continue;

      const runText = t.textContent || "";
      if (!runText.trim()) continue; // Skip empty or whitespace-only runs

      // Find the text in the HTML paragraph, starting from where we left off
      const searchStart = Math.max(0, processedOffset);
      let textIndex = paraText.indexOf(runText, searchStart);
      if (textIndex === -1) {
        // Try from the beginning if not found from current position
        textIndex = paraText.indexOf(runText);
        if (textIndex === -1) continue;
        processedOffset = textIndex + runText.length;
      } else {
        processedOffset = textIndex + runText.length;
      }

      // Get underline
      const runStyleId = rPr
        ? getAttr(getFirst(rPr, "w:rStyle"), "w:val") ?? undefined
        : undefined;
      const rawRunStyleUnderline = runStyleId
        ? resolveRawRunStyleUnderline(runStyleId)
        : undefined;
      const styleRunProps = runStyleId
        ? resolveRunStyleProps(runStyleId)
        : {};
      const directRunProps = extractRunProps(rPr);
      const effectiveRunProps = mergeRunProps(
        mergeRunProps(
          mergeRunProps(
            mergeRunProps(docDefaultRunProps, paragraphRunProps),
            rawRunStyleUnderline == undefined
              ? {}
              : {
                  underline: rawRunStyleUnderline,
                }
          ),
          styleRunProps
        ),
        directRunProps
      );
      const needsUnderline =
        directRunProps.underline ?? effectiveRunProps.underline ?? false;

      // Get color
      const color = getFirst(rPr, "w:color");
      const colorVal = getAttr(color, "w:val");
      const needsColor =
        colorVal && colorVal !== "auto" && colorVal !== "000000";

      // Get highlight - Word uses w:highlight with color name values
      const highlight = getFirst(rPr, "w:highlight");
      const highlightColorName = getAttr(highlight, "w:val");

      // Also check for shading (w:shd) as fallback
      const shd = getFirst(rPr, "w:shd");
      const highlightFill = getAttr(shd, "w:fill");
      const highlightVal = getAttr(shd, "w:val");

      // Convert Word highlight color name to hex, or use shading fill if available
      let highlightHex: string | null = null;
      if (highlightColorName) {
        highlightHex = useWordHighlightColorToHex(highlightColorName);
      } else if (highlightFill) {
        // Fallback to shading fill if no w:highlight found
        highlightHex = highlightFill.startsWith("#")
          ? highlightFill
          : `#${highlightFill}`;
      }

      // Check for highlight - exclude auto, white, black, nil, and clear values
      const needsHighlight =
        highlightHex &&
        highlightHex !== "#auto" &&
        highlightHex !== "#FFFFFF" &&
        highlightHex !== "#ffffff" &&
        highlightHex !== "#000000" &&
        highlightHex !== "#nil" &&
        highlightVal !== "clear";

      if (needsUnderline || needsColor || needsHighlight) {
        // Find the text node containing this text and wrap it
        const walker = htmlDoc.createTreeWalker(
          htmlPara,
          NodeFilter.SHOW_TEXT,
          null
        );

        let currentNode: Node | null = null;
        let currentOffset = 0;

        while ((currentNode = walker.nextNode())) {
          const nodeText = currentNode.textContent || "";
          const nodeStart = currentOffset;
          const nodeEnd = currentOffset + nodeText.length;

          if (textIndex >= nodeStart && textIndex < nodeEnd) {
            // Found the text node - wrap it in a span with styles
            const span = htmlDoc.createElement("span");
            if (needsUnderline) {
              // Use text-decoration-line for Quill clipboard matcher
              span.style.textDecorationLine = "underline";
            }
            if (needsColor) {
              const hexColor = colorVal.startsWith("#")
                ? colorVal
                : `#${colorVal}`;
              span.style.color = hexColor;
            }
            if (needsHighlight && highlightHex) {
              // Use background-color (with hyphen) for style extraction
              span.style.setProperty("background-color", highlightHex);
            }

            // Extract the specific text portion if needed
            const startInNode = textIndex - nodeStart;
            const endInNode = Math.min(
              startInNode + runText.length,
              nodeText.length
            );

            if (startInNode === 0 && endInNode === nodeText.length) {
              // Replace entire text node
              currentNode.parentNode?.replaceChild(span, currentNode);
              span.appendChild(currentNode);
            } else {
              // Split the text node and wrap the relevant portion
              const beforeText = nodeText.substring(0, startInNode);
              const targetText = nodeText.substring(startInNode, endInNode);
              const afterText = nodeText.substring(endInNode);

              if (beforeText) {
                currentNode.parentNode?.insertBefore(
                  htmlDoc.createTextNode(beforeText),
                  currentNode
                );
              }

              const targetNode = htmlDoc.createTextNode(targetText);
              span.appendChild(targetNode);
              currentNode.parentNode?.insertBefore(span, currentNode);

              if (afterText) {
                const afterNode = htmlDoc.createTextNode(afterText);
                currentNode.parentNode?.insertBefore(afterNode, currentNode);
              }

              currentNode.parentNode?.removeChild(currentNode);
            }
            break;
          }

          currentOffset = nodeEnd;
        }
      }
    }

    // Insert tabs AFTER processing all runs so text matching indices are correct
    if (tabText) {
      // Replace leading literal tabs already emitted by Mammoth instead of
      // duplicating them when adding the preserved zero-width-space pattern.
      const walker = htmlDoc.createTreeWalker(htmlPara, NodeFilter.SHOW_TEXT);
      let remainingTabs = leadingTabCount;
      let node: Node | null;
      while (remainingTabs > 0 && (node = walker.nextNode())) {
        const text = node.textContent || "";
        const count = Math.min(text.match(/^\t*/)?.[0].length ?? 0, remainingTabs);
        node.textContent = text.slice(count);
        remainingTabs -= count;
        if (count < text.length) break;
      }
      // Always insert tabs at the very beginning of the paragraph, before any elements
      // This ensures tabs appear first regardless of paragraph structure
      // Use zero-width space + tab pattern to preserve tabs in HTML serialization
      const tabNode = htmlDoc.createTextNode(tabText);
      htmlPara.insertBefore(tabNode, htmlPara.firstChild);
    }
  }

  // Get the updated HTML
  return useNormalizeWordDocumentLists(
    useRestoreWordDocumentNumbering(documentXml, numberingXml, htmlDiv.innerHTML)
  );
};
